/**
 * ROOM DIMENSIONS COMPONENT
 * 
 * Input fields for length, width, ceiling height
 * with auto-calculated floor SF, wall SF, and perimeter
 */

import React, { useEffect, useState } from 'react';

const RoomDimensions = ({ dimensions = {}, onChange, disabled = false }) => {
  const [localDims, setLocalDims] = useState({
    length_ft: dimensions.length_ft || '',
    length_in: dimensions.length_in || '',
    width_ft: dimensions.width_ft || '',
    width_in: dimensions.width_in || '',
    ceiling_height_ft: dimensions.ceiling_height_ft || '',
    ceiling_height_in: dimensions.ceiling_height_in || '',
  });

  // Calculate derived values
  const calculateValues = () => {
    const length = (parseFloat(localDims.length_ft) || 0) + (parseFloat(localDims.length_in) || 0) / 12;
    const width = (parseFloat(localDims.width_ft) || 0) + (parseFloat(localDims.width_in) || 0) / 12;
    const height = (parseFloat(localDims.ceiling_height_ft) || 0) + (parseFloat(localDims.ceiling_height_in) || 0) / 12;
    
    const floor_sf = length > 0 && width > 0 ? (length * width).toFixed(2) : null;
    const perimeter_lf = length > 0 && width > 0 ? (2 * (length + width)).toFixed(2) : null;
    const wall_sf = perimeter_lf && height > 0 ? (parseFloat(perimeter_lf) * height).toFixed(2) : null;
    
    return { floor_sf, wall_sf, perimeter_lf };
  };

  const { floor_sf, wall_sf, perimeter_lf } = calculateValues();

  // Update parent when local dims change
  useEffect(() => {
    const calc = calculateValues();
    onChange({
      ...localDims,
      length_ft: localDims.length_ft ? Number(localDims.length_ft) : null,
      length_in: localDims.length_in ? Number(localDims.length_in) : null,
      width_ft: localDims.width_ft ? Number(localDims.width_ft) : null,
      width_in: localDims.width_in ? Number(localDims.width_in) : null,
      ceiling_height_ft: localDims.ceiling_height_ft ? Number(localDims.ceiling_height_ft) : null,
      ceiling_height_in: localDims.ceiling_height_in ? Number(localDims.ceiling_height_in) : null,
      floor_sf: calc.floor_sf ? Number(calc.floor_sf) : null,
      wall_sf: calc.wall_sf ? Number(calc.wall_sf) : null,
      perimeter_lf: calc.perimeter_lf ? Number(calc.perimeter_lf) : null,
    });
  }, [localDims]);

  const handleChange = (field, value) => {
    setLocalDims(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  return (
    <div className="p-4 bg-slate-800/50 border border-slate-700 rounded space-y-4">
      <h4 className="text-sm font-semibold text-emerald-400 uppercase tracking-wider">
        Room Dimensions
      </h4>
      
      <div className="grid grid-cols-3 gap-4">
        {/* Length */}
        <div>
          <label className="block text-xs text-slate-500 mb-1">Length</label>
          <div className="flex gap-1 items-center">
            <input
              type="number"
              min="0"
              value={localDims.length_ft}
              onChange={(e) => handleChange('length_ft', e.target.value)}
              disabled={disabled}
              placeholder="0"
              className="w-16 px-2 py-1.5 bg-slate-950 border border-slate-700 text-slate-100 text-sm rounded-sm
                focus:border-emerald-500 focus:outline-none disabled:opacity-50"
            />
            <span className="text-xs text-slate-500">ft</span>
            <input
              type="number"
              min="0"
              max="11"
              value={localDims.length_in}
              onChange={(e) => handleChange('length_in', e.target.value)}
              disabled={disabled}
              placeholder="0"
              className="w-12 px-2 py-1.5 bg-slate-950 border border-slate-700 text-slate-100 text-sm rounded-sm
                focus:border-emerald-500 focus:outline-none disabled:opacity-50"
            />
            <span className="text-xs text-slate-500">in</span>
          </div>
        </div>
        
        {/* Width */}
        <div>
          <label className="block text-xs text-slate-500 mb-1">Width</label>
          <div className="flex gap-1 items-center">
            <input
              type="number"
              min="0"
              value={localDims.width_ft}
              onChange={(e) => handleChange('width_ft', e.target.value)}
              disabled={disabled}
              placeholder="0"
              className="w-16 px-2 py-1.5 bg-slate-950 border border-slate-700 text-slate-100 text-sm rounded-sm
                focus:border-emerald-500 focus:outline-none disabled:opacity-50"
            />
            <span className="text-xs text-slate-500">ft</span>
            <input
              type="number"
              min="0"
              max="11"
              value={localDims.width_in}
              onChange={(e) => handleChange('width_in', e.target.value)}
              disabled={disabled}
              placeholder="0"
              className="w-12 px-2 py-1.5 bg-slate-950 border border-slate-700 text-slate-100 text-sm rounded-sm
                focus:border-emerald-500 focus:outline-none disabled:opacity-50"
            />
            <span className="text-xs text-slate-500">in</span>
          </div>
        </div>
        
        {/* Ceiling Height */}
        <div>
          <label className="block text-xs text-slate-500 mb-1">Ceiling Height</label>
          <div className="flex gap-1 items-center">
            <input
              type="number"
              min="0"
              value={localDims.ceiling_height_ft}
              onChange={(e) => handleChange('ceiling_height_ft', e.target.value)}
              disabled={disabled}
              placeholder="8"
              className="w-16 px-2 py-1.5 bg-slate-950 border border-slate-700 text-slate-100 text-sm rounded-sm
                focus:border-emerald-500 focus:outline-none disabled:opacity-50"
            />
            <span className="text-xs text-slate-500">ft</span>
            <input
              type="number"
              min="0"
              max="11"
              value={localDims.ceiling_height_in}
              onChange={(e) => handleChange('ceiling_height_in', e.target.value)}
              disabled={disabled}
              placeholder="0"
              className="w-12 px-2 py-1.5 bg-slate-950 border border-slate-700 text-slate-100 text-sm rounded-sm
                focus:border-emerald-500 focus:outline-none disabled:opacity-50"
            />
            <span className="text-xs text-slate-500">in</span>
          </div>
        </div>
      </div>
      
      {/* Calculated Values */}
      {(floor_sf || wall_sf || perimeter_lf) && (
        <div className="flex gap-6 pt-2 border-t border-slate-700/50">
          {floor_sf && (
            <div className="text-center">
              <div className="text-lg font-bold text-emerald-400">{floor_sf}</div>
              <div className="text-xs text-slate-500">Floor SF</div>
            </div>
          )}
          {wall_sf && (
            <div className="text-center">
              <div className="text-lg font-bold text-slate-300">{wall_sf}</div>
              <div className="text-xs text-slate-500">Wall SF</div>
            </div>
          )}
          {perimeter_lf && (
            <div className="text-center">
              <div className="text-lg font-bold text-slate-300">{perimeter_lf}</div>
              <div className="text-xs text-slate-500">Perimeter LF</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RoomDimensions;
